import React from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';

import { useAppSelector } from '@/store';

import {
  PageShell,
  BackHeader,
  MetricCard,
  MetricGrid,
  SectionTitle,
  ProgressBar,
  Toggle,
  Icon,
  Text,
  SpentOfBudget,
  DrillCard,
  LoadMore,
  Skeleton,
} from '../../components';
import { ACQ, CHANNEL_CARD_TONE } from '../../theme/tokens';
import type { ChannelState } from '../../theme/tokens';
import { ROUTE } from '../../routes';
import { usePagination } from '../../hooks/usePagination';
import { useAcquisitionChannelGate } from '../../hooks/useAcquisitionChannelGate';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import type { ChannelCampaign, ChannelId } from '../../mock-data/types';
import { markerText } from '../acquisition/performance-stats';
import { useChannelPerformance } from './useChannelPerformance';
import { useChannelCampaigns } from './useChannelCampaigns';
import { useChannelBudget } from './useChannelBudget';
import { CHANNEL_CHROME, CHANNEL_CPA_SCAFFOLD, CHANNEL_METRIC_SCAFFOLD } from './channel-network';
import {
  TopGroup,
  TopRow,
  Disclaimer,
  DisclaimerIcon,
  DisclaimerText,
  Advisory,
  AdvisorySpark,
  AdvisoryText,
  Summary,
  SummaryTop,
  SummaryName,
  Name,
  BarGroup,
  BudgetStatus,
  SpentSolo,
  ManageButton,
  CampaignTable,
  CampaignList,
  Divider,
  EnabledRow,
  EnabledLabel,
  StatTiles,
  StatTile,
  StatTileLabel,
  StatTileValue,
  SkeletonCard,
  SkeletonHead,
  SkeletonTiles,
  Empty,
  EmptyText,
  Note,
  NoteHead,
  NoteSpark,
  NoteTitle,
  NoteDesc,
} from './channel-detail.styles';
import BudgetLimitsModal from '../acquisition/components/AdBudgetLimit/BudgetLimitsModal';

const SPARK_ICON = '/assets/acq/lumio-spark.svg';

// "Sunday, June 21, 2026": the previous calendar day, which the budget figures cover.
const prettyYesterday = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

// Campaigns load 4 at a time; "Load more campaigns" reveals the next page.
const CAMPAIGNS_PER_PAGE = 4;

type StatField = { key: keyof ChannelCampaign['stats']; label: string };

const STAT_FIELDS: StatField[] = [
  { key: 'dailySpend', label: 'Daily Spend' },
  { key: 'registrations', label: 'Regs' },
  { key: 'ftds', label: 'FTDs' },
  { key: 'ftdValue', label: 'FTD Val' },
];

/**
 * Budget pacing from the channel's real spend vs its daily limit: the bar's fill
 * percentage and the health state that tints the cards. With no limit (the
 * acquisition-budget API exposes none for the channel) the bar stays empty and the
 * card reads neutral.
 */
const deriveBudgetState = (spent: number, limit: number | undefined): { progress: number; state: ChannelState } => {
  if (!limit || limit <= 0) return { progress: 0, state: 'active' };
  const progress = Math.min(100, Math.max(0, Math.round((spent / limit) * 100)));
  const state: ChannelState = progress >= 100 ? 'exhausted' : progress >= 90 ? 'nearing-budget' : 'active';
  return { progress, state };
};

export type ChannelDetailViewProps = {
  /** Drives the per-channel network / `aft` filters for the live figures + campaigns. */
  channelId: ChannelId;
  styleText?: string;
  className?: string;
};

/**
 * Channel detail — CPA card + budget summary (both tinted by the channel's budget-pacing
 * health) + today's metrics + the channel's REAL campaigns (each toggleable) + a Lumio
 * note. Every figure is API-sourced: the campaigns + their spend come from the Casino Ad
 * Service, the conversion metrics from the Owners Hub Stats API, the daily limit from the
 * acquisition-budget API. Static chrome (names, labels) is held per channel, not fetched.
 */
export const ChannelDetailView = ({
  channelId,
  styleText = '',
  className = '',
}: ChannelDetailViewProps): React.ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // The per-campaign enable/disable toggle is a build-ahead control (local UI intent, not a
  // backed action yet), so it ships hidden behind the `channel-campaign-toggle` feature flag —
  // no veil, just absent. Each card still reflects its real provider status: it opens the
  // running or paused detail accordingly, and a disabled campaign's stat tiles stay muted.
  // Revealed live for QA via `show_in_progress`.
  const { isLocked } = useFeatureGate();
  const campaignToggleLocked = isLocked('channel-campaign-toggle');

  // The displayed (lagged) brand scopes the live figures, same source the rest of the
  // area reads so content tracks the brand switch through the transition fade.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);
  const brandId = displayBrand?.id;
  const tenantId = displayBrand?.tenant_id;

  const chrome = CHANNEL_CHROME[channelId];

  // Three sources, each filling in on its own resolve. Campaigns + channel ad-spend (Ad
  // Service); the CPA card + Today's Performance grid (Stats, plus the ad-spend for Daily
  // Spend + CPA); the daily budget limit (acquisition-budget API).
  const channel = useChannelCampaigns(channelId, tenantId, brandId);
  const perf = useChannelPerformance(channelId, brandId, CHANNEL_METRIC_SCAFFOLD, CHANNEL_CPA_SCAFFOLD, {
    costToday: channel.channelCostToday,
    costYesterday: channel.channelCostYesterday,
    errored: channel.costErrored,
    loading: channel.loading,
  });
  const budget = useChannelBudget(brandId, channelId);

  // Computed once per mount: the budget figures reflect the prior day, so the footnote
  // names that date rather than "yesterday" in the abstract.
  const reportDate = React.useMemo(prettyYesterday, []);

  // Budget summary: real channel spend vs the channel daily limit. When the ad-spend
  // request failed the spend is UNKNOWN (not zero), so the figure reads `N/A` (matching the
  // CPA card + Daily Spend tile) and the bar is suppressed rather than asserting 0% pacing.
  const spentErrored = channel.costErrored;
  const spent = Math.round(channel.channelCostToday ?? 0);
  const hasLimit = typeof budget.limit === 'number' && budget.limit > 0;
  const summaryLoading = channel.loading || budget.loading;
  const { progress, state } = deriveBudgetState(spent, budget.limit);
  const accent = CHANNEL_CARD_TONE[state].accent;
  // Show the budget bar / denominator while loading (optimistic, avoids a pop-in) and once
  // a real limit is known; not on a spend failure, and the limit-less channel falls back to
  // spend-only.
  const showBudgetBar = !spentErrored && (summaryLoading || hasLimit);

  // Categorical metric / column labels translated reactively at the render site over a
  // fixed, closed UI vocabulary; an unknown value falls through untranslated.
  const metricLabels: Record<string, string> = {
    'Cost per Acquisition': t('Cost per Acquisition'),
    Registrations: t('Registrations'),
    FTDs: t('FTDs'),
    'Avg FTD Value': t('Avg FTD Value'),
    'Daily Spend': t('Daily Spend'),
    Regs: t('Regs'),
    'FTD Val': t('FTD Val'),
  };
  const metricLabel = (label: string): string => metricLabels[label] ?? label;

  // Channel (network) names — a fixed, closed set of chrome labels, translated at the
  // render site. Unknown names fall through untranslated.
  const channelLabels: Record<string, string> = {
    'Web Networks': t('Web Networks'),
    'App Networks': t('App Networks'),
    'Crypto Sites': t('Crypto Sites'),
    'Short Videos': t('Short Videos'),
  };
  const channelLabel = (name: string): string => channelLabels[name] ?? name;

  // The budget bar grows in once its data arrives: `revealed` flips false→true a frame
  // after loading resolves, driving the fill transition.
  const [revealed, setRevealed] = React.useState(false);
  React.useEffect(() => {
    if (summaryLoading) {
      setRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [summaryLoading]);

  // Client-side pagination of the loaded campaign list. "Load more campaigns" reveals the
  // next page; the full list is already in hand (the API page max is 100, ample here).
  const { visible: visibleCampaigns, hasMore, loadMore } = usePagination(channel.items, CAMPAIGNS_PER_PAGE);

  // Per-campaign enable/disable is local UI intent (the disclaimer notes edits land the
  // next day). Seed each card once from its real provider status (Running → on); a later
  // list refresh seeds only NEW ids, so it never clobbers a toggle the owner just flipped.
  const [enabled, setEnabled] = React.useState<Record<string, boolean>>({});
  React.useEffect(() => {
    setEnabled((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const campaign of channel.items) {
        if (!(campaign.id in next)) {
          next[campaign.id] = campaign.enabled;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [channel.items]);

  // Whole card opens the campaign detail (id = the card's detailId).
  const onOpenCampaign = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) navigate(ROUTE.campaignDetail(id));
    },
    [navigate],
  );

  // Toggle lives inside the clickable card — stop the click/key from bubbling up to the
  // card so flipping the switch never also navigates.
  const onToggleCampaign = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const id = event.currentTarget.dataset.id;
    if (!id) return;
    setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const onToggleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
  }, []);

  // "Manage budget and limits" opens the shared Lumio budget/limits modal.
  const [openBudgetDialog, setOpenBudgetDialog] = React.useState(false);
  const onOpenBudgetLimits = React.useCallback(() => setOpenBudgetDialog(true), []);
  const onCloseBudgetLimits = React.useCallback(() => setOpenBudgetDialog(false), []);

  // CPA advisory — only when the channel is exhausted or nearing budget (no exhaustion
  // time is available, so the copy stays time-less).
  const advisory =
    state === 'active' ? undefined : (
      <Advisory>
        <AdvisorySpark src={SPARK_ICON} alt="" />
        {state === 'exhausted' ? (
          <AdvisoryText
            text="Lumio found profitable traffic but reached the daily budget. Consider increasing the daily limit to keep players coming."
            tag="paragraph"
          />
        ) : (
          <AdvisoryText
            text="Nearing daily budget. Consider increasing the daily limit to keep players coming."
            tag="paragraph"
          />
        )}
      </Advisory>
    );

  // Budget status label under the bar — only in a coloured state.
  const budgetStatus =
    state === 'exhausted' ? (
      <BudgetStatus $color={accent} text="Daily budget reached" tag="span" />
    ) : state === 'nearing-budget' ? (
      <BudgetStatus $color={accent} text="Nearing budget" tag="span" />
    ) : null;

  return (
    <PageShell styleText={styleText} className={className}>
      <BackHeader title={channelLabel(chrome.title)} />

      <TopGroup>
        <TopRow>
          <MetricCard
            label={metricLabel(perf.cpa.label)}
            value={markerText(perf.cpa)}
            change={perf.cpa.change}
            changeType={perf.cpa.changeType}
            flat={perf.cpa.flat}
            vs={t('vs yesterday')}
            tone={state}
            raised
            invertDelta
            note={advisory}
            loading={perf.loading}
          />

          <Summary $tone={state}>
            <SummaryTop>
              <SummaryName>
                <Icon name={chrome.icon} size={22} color={ACQ.t300} />
                <Name text={channelLabel(chrome.name)} tag="span" />
              </SummaryName>
              {spentErrored ? (
                <SpentSolo>
                  <Text text="Spent" tag="span" />
                  <span>N/A</span>
                </SpentSolo>
              ) : showBudgetBar ? (
                <SpentOfBudget spent={spent} budget={budget.limit ?? 0} withLabel loading={summaryLoading} />
              ) : (
                <SpentSolo>
                  <Text text="Spent" tag="span" />
                  <span>${spent}</span>
                </SpentSolo>
              )}
            </SummaryTop>
            {showBudgetBar ? (
              <BarGroup>
                <ProgressBar value={progress} barColor={accent} animate={revealed} loading={summaryLoading} />
                {summaryLoading ? null : budgetStatus}
              </BarGroup>
            ) : null}
            <ManageButton
              label={t('Manage budget and limits')}
              variant="outline"
              icon="edit"
              fullWidth
              onClick={onOpenBudgetLimits}
            />

            <BudgetLimitsModal isOpen={openBudgetDialog} onClose={onCloseBudgetLimits} />
          </Summary>
        </TopRow>

        <Disclaimer>
          <DisclaimerIcon>
            <Icon name="info" size={14} color={ACQ.t400} />
          </DisclaimerIcon>
          <DisclaimerText
            tag="span"
            text="Data shown is from {{date}}. Any limit and budget adjustments take effect the following day."
            placeholders={{ date: reportDate }}
          />
        </Disclaimer>
      </TopGroup>

      <div>
        <SectionTitle title={t("Today's Performance")} />
        <MetricGrid>
          {perf.metrics.map((metric) => (
            <MetricCard
              key={metric.id}
              surface="panel"
              label={metricLabel(metric.label)}
              value={markerText(metric)}
              change={metric.change}
              changeType={metric.changeType}
              flat={metric.flat}
              vs={t('vs yesterday')}
              muted={metric.muted}
              // Marker figures (`?`/`N/A`/`–`) show immediately (no skeleton).
              loading={perf.loading && !metric.missing && !metric.error && !metric.indeterminate}
            />
          ))}
        </MetricGrid>
      </div>

      <div>
        <SectionTitle title={t('Campaigns in {{channel}}')} placeholders={{ channel: channelLabel(chrome.name) }} />
        <CampaignTable>
          <CampaignList>
            {channel.loading ? (
              Array.from({ length: CAMPAIGNS_PER_PAGE }).map((_, index) => (
                <SkeletonCard key={index}>
                  <SkeletonHead>
                    <Skeleton width={60} height={60} radius={16} />
                    <Skeleton width="70%" height={18} radius={6} />
                  </SkeletonHead>
                  <Divider />
                  <SkeletonTiles>
                    {STAT_FIELDS.map((field) => (
                      <StatTile key={field.key}>
                        <Skeleton width="3ch" height={12} radius={5} />
                        <Skeleton width="4ch" height={16} radius={5} />
                      </StatTile>
                    ))}
                  </SkeletonTiles>
                </SkeletonCard>
              ))
            ) : channel.items.length === 0 ? (
              <Empty>
                <Icon name="sparkle" size={24} color={ACQ.t400} />
                <EmptyText
                  text="No campaigns are running in {{channel}} yet."
                  placeholders={{ channel: channelLabel(chrome.name) }}
                  tag="span"
                />
              </Empty>
            ) : (
              visibleCampaigns.map((campaign) => {
                const isOn = enabled[campaign.id] ?? campaign.enabled;
                // Enabled → the running detail; disabled → the paused detail (the same
                // real campaign id either way; the detail page reflects its true status).
                const detailId = isOn ? campaign.runningDetailId : campaign.pausedDetailId;
                return (
                  <DrillCard
                    key={campaign.id}
                    image={campaign.image}
                    eyebrow={t('Campaign period {{period}}')}
                    eyebrowPlaceholders={{ period: campaign.period }}
                    title={campaign.title}
                    id={detailId}
                    onOpen={onOpenCampaign}
                  >
                    {!campaignToggleLocked ? (
                      <>
                        <Divider />
                        <EnabledRow onKeyDown={onToggleKeyDown}>
                          <EnabledLabel
                            text="Enabled in {{channel}}"
                            placeholders={{ channel: channelLabel(chrome.name) }}
                            tag="span"
                          />
                          <Toggle
                            on={isOn}
                            label={t('Toggle {{name}}')}
                            labelValues={{ name: campaign.title }}
                            id={campaign.id}
                            onToggle={onToggleCampaign}
                          />
                        </EnabledRow>
                      </>
                    ) : null}
                    <Divider />
                    <StatTiles $muted={!isOn}>
                      {STAT_FIELDS.map((field) => (
                        <StatTile key={field.key}>
                          <StatTileLabel text={metricLabel(field.label)} tag="span" />
                          <StatTileValue>{campaign.stats[field.key]}</StatTileValue>
                        </StatTile>
                      ))}
                    </StatTiles>
                  </DrillCard>
                );
              })
            )}
          </CampaignList>
          {!channel.loading && hasMore ? <LoadMore label={t('Load more campaigns')} onClick={loadMore} /> : null}
        </CampaignTable>
      </div>

      <Note>
        <NoteHead>
          <NoteSpark src={SPARK_ICON} alt="" />
          <NoteTitle text="Lumio Managed Channel" tag="span" />
        </NoteHead>
        <NoteDesc
          text="Creatives, targeting and bid optimization are handled automatically. Toggle campaigns on/off and manage your daily budget, Lumio does the rest."
          tag="paragraph"
        />
      </Note>
    </PageShell>
  );
};

export type ChannelDetailProps = {
  styleText?: string;
  className?: string;
};

/**
 * Route entry for `account/marketing/network/:id` (one route, all networks). Resolves the
 * channel id and renders the view. `key={id}` remounts the view per channel so per-campaign
 * toggle state never leaks across networks.
 */
const ChannelDetail = ({ styleText = '', className = '' }: ChannelDetailProps): React.ReactElement | null => {
  const { id } = useParams<{ id: string }>();
  // Reactive gate: re-evaluates the redirect when a channel is unlocked/locked at runtime.
  const { isEnabled, revealed } = useAcquisitionChannelGate();

  if (!id || !(id in CHANNEL_CHROME)) return null;
  // Gated channels (release feature flag) have no reachable detail page: their cards are
  // inert everywhere, so guard direct-URL access too by bouncing back to Acquisition. The
  // Lumio sections that link here show only under the plain "777" reveal, so without it
  // every channel page bounces the same way.
  if (!revealed || !isEnabled(id)) return <Navigate to={ROUTE.acquisition()} replace />;
  return (
    <ChannelDetailView key={id} channelId={id as ChannelId} styleText={styleText} className={className} />
  );
};

export default ChannelDetail;
